import FocusTrap from 'focus-trap-react';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { AriaRoles, messages as m } from '../../constants/AccessibilityConstants';
import DropdownConstants from '../../constants/Dropdown';
import QA from '../../constants/QATags';
import { getDropdown } from '../../tools/Dropdowns/DropdownUtils';

import './Dropdown.scss';

export default class extends React.Component {
  static propTypes = {
    dropdowns: PropTypes.array,
    closeDropdown: PropTypes.func,
  };

  onClose = () => {
    const dropdownConfig = this.props.dropdowns[1];
    const { onCloseDropdown } = dropdownConfig;
    // Callee specified additional dropdown closing function
    if (onCloseDropdown) {
      onCloseDropdown();
    }
    this.props.closeDropdown();
  };

  onCloseEscape = () => {
    this.props.closeDropdown();
  };

  render() {
    if (!this.props.dropdowns.length) {
      return null;
    }

    const [dropdownType, dropdownConfig] = this.props.dropdowns;
    const {
      position,
      positionAdjustments,
      alignHorizontal = DropdownConstants.ALIGN_LEFT,
      alignVertical = DropdownConstants.ALIGN_BOTTOM,
      ...childProps
    } = dropdownConfig;
    const propsToPass = {
      closeDropdown: this.onClose,
      dropdownType,
      childProps,
    };

    const dropdownEl = getDropdown(dropdownType, propsToPass);
    const margin = 2;
    const adjustments = positionAdjustments || {};
    const positionAdjustment = adjustments.position || 'absolute';
    const horizontalAdjustment = adjustments.horizontal || 0;
    const verticalAdjustment = adjustments.vertical || 0;
    const focusTrapOptions = {
      escapeDeactivates: true,
      clickOutsideDeactivates: true,
      onDeactivate: this.onCloseEscape,
      returnFocusOnDeactivate: true,
    };

    return (
      <FocusTrap
        tag='nav'
        focusTrapOptions={focusTrapOptions}
        active={true}
        onScroll={this.onClose}
      >
        <div
          className='Dropdown'
          onScroll={this.onClose}
          onMouseLeave={this.onClose}
          role={AriaRoles.SUBMENU}
          ref={box => {
            // ensure dropdown doesn't get cut off when rendered
            if (box && window) {
              let windowConstrainedLeft;
              let unconstrainedLeft;
              if (alignHorizontal === DropdownConstants.ALIGN_LEFT) {
                unconstrainedLeft = position.left + horizontalAdjustment;
                windowConstrainedLeft = window.innerWidth - box.offsetWidth;
              } else {
                unconstrainedLeft =
                  position.right + horizontalAdjustment + margin - box.offsetWidth;
                windowConstrainedLeft = window.innerWidth;
              }
              const height = alignVertical === DropdownConstants.ALIGN_TOP ? 0 : position.height;
              box.style.position = positionAdjustment;
              const yOffset = adjustments.position === 'fixed' ? 0 : window.pageYOffset;
              box.style.top =
                Math.min(
                  position.top + yOffset + verticalAdjustment + height - margin,
                  Math.max(window.innerHeight + yOffset - box.offsetHeight, 0),
                ) + 'px';
              box.style.left = Math.min(unconstrainedLeft, windowConstrainedLeft) + 'px';
              // The hover shield acts like an invisible buffer outside of the element that when hovered out of will
              // close the dropdown
              box.style.margin = `-${DropdownConstants.HOVER_SHIELD_SIZE_PX}px`;
              box.style.padding = `${DropdownConstants.HOVER_SHIELD_SIZE_PX}px`;
              // override if we have cutoffs
              if (box.getBoundingClientRect().right > windowConstrainedLeft) {
                box.style.marginRight = '0px';
                box.style.paddingRight = '0px';
              }
            }
          }}
          data-qa={QA.DD}
        >
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
          <p className='Dropdown__description__hidden visuallyHidden' tabIndex='0'>
            <FormattedMessage {...m.menuTrapScreenReaderText} />{' '}
          </p>
          {dropdownEl}
        </div>
      </FocusTrap>
    );
  }
}
